import { AssignmentTurnedIn } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  assignCostCenterAndFinancialNode,
  getCostCenters,
  getFinancialNodes,
} from '../../../../../../../lib/api/accounting';

interface CostCenter {
  id: number;
  name: string;
  code: string;
}

interface FinancialNode {
  id: number;
  key: string;
  accountCode: string;
}

export interface Document {
  id: number;
  folio: string;
  businessIdentifier?: string;
  stakeholderIdentifier?: string;
  businessName?: string;
  stakeholderName?: string;
  subType: string;
  amount: number;
  costCenterId: number | null;
  financialNodeId: number | null;
}

interface AssignmentData {
  documentId: number;
  costCenterId: number | null;
  financialNodeId: number | null;
}

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
  onAssignmentComplete: () => void;
}
const useStyles = makeStyles(() =>
  createStyles({
    iconButton: {
      padding: '6px',
      margin: '0 2px',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },

    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },

    actionsCell: {
      minWidth: '80px',
      maxWidth: '100px',
      padding: '8px !important',
    },
  }),
);
const AssignmentDialog: React.FC<AssignmentDialogProps> = ({
  open,
  onClose,
  documents,
  onAssignmentComplete,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [financialNodes, setFinancialNodes] = useState<FinancialNode[]>([]);
  const [assignments, setAssignments] = useState<AssignmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inicializar assignments cuando se abren los documentos
  useEffect(() => {
    if (open && documents.length > 0) {
      const initialAssignments = documents.map((doc) => ({
        documentId: doc.id,
        costCenterId: doc.costCenterId,
        financialNodeId: doc.financialNodeId,
      }));
      setAssignments(initialAssignments);
    }
  }, [open, documents]);

  // Cargar centros de costos y financial nodes
  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([getCostCenters(dispatch), getFinancialNodes(dispatch)])
        .then(([costCentersData, financialNodesData]) => {
          setCostCenters(costCentersData);
          setFinancialNodes(financialNodesData);
        })
        .catch((error) => {
          console.error('Error loading data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, dispatch]);

  const handleAssignmentChange = (
    documentId: number,
    field: 'costCenterId' | 'financialNodeId',
    value: number | null,
  ) => {
    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.documentId === documentId
          ? { ...assignment, [field]: value }
          : assignment,
      ),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filtrar solo las asignaciones que tienen al menos un valor
      const validAssignments = assignments.filter(
        (assignment) =>
          assignment.costCenterId !== null ||
          assignment.financialNodeId !== null,
      );

      if (validAssignments.length === 0) {
        alert('Debe asignar al menos un centro de costos o cuenta contable');
        return;
      }

      await assignCostCenterAndFinancialNode(dispatch, validAssignments);

      onAssignmentComplete();
      onClose();
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Error al guardar las asignaciones');
    } finally {
      setSaving(false);
    }
  };

  const getDocumentById = (id: number) => {
    return documents.find((doc) => doc.id === id);
  };

  const getCostCenterById = (id: number | null) => {
    if (!id) return null;
    return costCenters.find((cc) => cc.id === id);
  };

  const getFinancialNodeById = (id: number | null) => {
    if (!id) return null;
    return financialNodes.find((fn) => fn.id === id);
  };
  const assignConditionsForAllDocumentsBelow = (documentId: number) => {
    const currentAssignment = assignments.find(
      (assignment) => assignment.documentId === documentId,
    );

    if (!currentAssignment) return;

    const currentIndex = assignments.findIndex(
      (assignment) => assignment.documentId === documentId,
    );

    if (currentIndex === -1) return;

    setAssignments((prev) =>
      prev.map((assignment, index) => {
        if (index > currentIndex) {
          return {
            ...assignment,
            costCenterId: currentAssignment.costCenterId,
            financialNodeId: currentAssignment.financialNodeId,
          };
        }
        return assignment;
      }),
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Asignación de Centro de Costos y Cuenta Contable
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CenteredCircularProgress type="dialog" />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Folio</TableCell>
                  <TableCell>Rut</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Monto</TableCell>
                  <TableCell>Centro de Costos</TableCell>
                  <TableCell>Cuenta Contable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => {
                  const document = getDocumentById(assignment.documentId);
                  if (!document) return null;

                  return (
                    <TableRow key={assignment.documentId}>
                      <TableCell className={classes.actionsCell}>
                        <Box display="flex" justifyContent="start">
                          <LightTooltip
                            placement="right"
                            title={
                              <Typography style={{ padding: 12 }}>
                                Copiar centro de costos y cuenta contable a
                                todas las filas de abajo
                              </Typography>
                            }
                          >
                            <IconButton
                              className={classes.iconButton}
                              onClick={() =>
                                assignConditionsForAllDocumentsBelow(
                                  assignment.documentId,
                                )
                              }
                            >
                              <AssignmentTurnedIn
                                className={classes.assignButton}
                              />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {document.folio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.businessIdentifier ||
                            document.stakeholderIdentifier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.businessName || document.stakeholderName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.subType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${document.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={costCenters}
                          getOptionLabel={(option) =>
                            option.code
                              ? `${option.code} - ${option.name}`
                              : `${option.name}`
                          }
                          value={getCostCenterById(assignment.costCenterId)}
                          onChange={(_, newValue) =>
                            handleAssignmentChange(
                              assignment.documentId,
                              'costCenterId',
                              newValue?.id || null,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Seleccionar centro de costos"
                            />
                          )}
                          sx={{ minWidth: 250 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={financialNodes}
                          getOptionLabel={(option) =>
                            option.accountCode
                              ? `${option.accountCode} - ${option.key}`
                              : `${option.key}`
                          }
                          value={getFinancialNodeById(
                            assignment.financialNodeId,
                          )}
                          onChange={(_, newValue) =>
                            handleAssignmentChange(
                              assignment.documentId,
                              'financialNodeId',
                              newValue?.id || null,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Seleccionar cuenta contable"
                            />
                          )}
                          sx={{ minWidth: 250 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <CustomButton onClick={onClose} disabled={saving} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} /> : 'Guardar'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentDialog;
